import React from 'react';
import _ from 'lodash';
import Joi from 'joi';
import PropTypes from 'prop-types';

import Conditional from '../../../common/conditional';
import Form from '../../../common/form';

import { getResultOptions } from '../../../../utils/data';
import { getFileUploadTypes } from '../../../../utils/utilities';
import { LIMITATION_OPTIONS, OTHER_DESCRIPTION, PEC_SCAN_LOCATIONS } from '../../../../utils/constants';
import { CoverDataItem } from '../../../../utils/models';

class PECScanDataItemNewForm extends Form {
	state = {
		data: {
			sectionName: '',
			scanLocation: '',
			limitation: [],
			images: [],
			nominalThickness: '',
			corrosionAllowance: '',
			result: '',
			otherResult: '',
			remarks: 'NG- Not Given; N/A- Not Applicable or Restricted area',
			readings: { data: [], circValues: [] },
		},
		errors: {},
		loading: false,
		progress: 0,
		scanLocationOptions: PEC_SCAN_LOCATIONS,
	};

	static propTypes = {
		onProceed: PropTypes.func,
	};

	schema = {
		sectionName: Joi.string().min(3).required().label('Scan Section Name'),
		scanLocation: Joi.object({
			label: Joi.string().required(),
			value: Joi.string().required(),
			__isNew__: Joi.boolean().optional(),
		}).required().label('Scan Location'),
		limitation: Joi.array().items(Joi.object({
			label: Joi.string().required(),
			value: Joi.string().required(),
			__isNew__: Joi.boolean().optional(),
		})).min(1).required().label('Limitation'),
		images: Joi.array().min(1).required().label('Scan Image'),
		nominalThickness: Joi.alternatives().try(Joi.number().positive(), Joi.string().valid('NG')).required().label('Nominal Thickness'),
		corrosionAllowance: Joi.alternatives().try(Joi.number().positive(), Joi.string().valid('NG')).required().label('Corrosion Allowance'),
		result: Joi.string().min(3).required().label('Result'),
		otherResult: Joi.string().when('result', {
			is: OTHER_DESCRIPTION,
			then: Joi.string().min(3).required().label('Result'),
			otherwise: Joi.string().allow('').label('Result'),
		}),
		remarks: Joi.string().min(3).required().label('Remarks'),
		readings: Joi.object({
			data: Joi.array().min(0).required().label('Data'),
			circValues: Joi.array().min(0).required().label('Circ. Values'),
		}),
	};

	getData = () => {
		const finalData = _.omit(this.state.data, ['otherResult', 'scanLocation']);
		const limitation = (this.state.data.limitation as unknown as CoverDataItem[]).map((item) => item.value).join(', ');

		return {
			...finalData,
			limitation,
			scanLocation: (this.state.data.scanLocation as unknown as CoverDataItem).value,
			result: this.state.data.result === OTHER_DESCRIPTION ? this.state.data.otherResult : this.state.data.result
		}
	};

	doSubmit = (): void => {
		this.props.onProceed(this.getData());

		this.setState({ 
			data: {
				sectionName: '',
				scanLocation: '',
				limitation: [],
				images: [],
				nominalThickness: '',
				corrosionAllowance: '',
				result: '',
				remarks: '',
				readings: { data: [], circValues: [] },
			}
		});
	};

	getResultOptions = () => {
		return [...getResultOptions(), { id: OTHER_DESCRIPTION, name: OTHER_DESCRIPTION }];
	};

	handleNewOptionCreation = (newOption: string) => {
		const newMultiSelectOption = this.createNewMultiSelectOption(newOption);

		const scanLocationOptions = [...this.state.scanLocationOptions, newMultiSelectOption];
		const data = { ...this.state.data, scanLocation: newMultiSelectOption } as any;

		this.setState({ data, scanLocationOptions } as any);
	};

	render() {
		return (
			<div className="p-4 rounded bg-light bordered mb-4">
				<form autoComplete="off" onSubmit={this.handleSubmit}>
					<h2 className="form-title">Scan Data Overview</h2>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'sectionName',
								placeholder: 'Enter Scan Section Name',
								label: 'Scan Section Name',
							})}
						</div>

						<div className="col">
							{this.renderCreatableMultiselect({
								closeMenuOnSelect: true,
								options: this.state.scanLocationOptions,
								name: 'scanLocation',
								placeholder: 'Enter Scan Location',
								label: 'Scan Location',
								isMulti: false,
								onCreateOption: this.handleNewOptionCreation
							})}
						</div>

						<div className="col">
							{this.renderCreatableMultiselect({
								name: 'limitation',
								placeholder: 'Select Limitation',
								label: 'Limitation',
								closeMenuOnSelect: true,
								options: LIMITATION_OPTIONS,
								isMulti: true,
							})}
						</div>

						<div className="col">
							{this.renderFileInput({
								max: 1,
								multiple: false,
								name: 'images',
								accept: 'image/*,application/pdf',
								label: 'Scan Image',
								sublabel: 'Upload Scan Image',
								types: getFileUploadTypes(),
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'nominalThickness',
								label: 'Nominal Thickness',
								placeholder: 'Enter Nominal Thickness',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'corrosionAllowance',
								label: 'Corrosion Allowance',
								placeholder: 'Enter Corrosion Allowance',
							})}
						</div>

						<div className="col">
							{this.renderSelect({
								name: 'result',
								label: 'Result',
								placeholder: 'Select Result',
								options: this.getResultOptions(),
							})}
						</div>
						
						<Conditional isVisible={this.state.data.result === OTHER_DESCRIPTION}>
							<div className="col">
								{this.renderInput({
									type: 'text',
									name: 'otherResult',
									label: 'Result',
									placeholder: 'Enter Result',
								})}
							</div>
						</Conditional>
					</div>

					<div className="my-4 divider"></div>

					{this.renderTextArea({
						rows: 4,
						name: 'remarks',
						label: 'Remarks',
						placeholder: 'Enter Remarks',
					})}
					
					<div className="mt-4 my-2 divider"></div>

					<div className="d-flex justify-content-end">
						{this.renderButton('Add Scan Row')}
					</div>
				</form>
			</div>
		);
	}
}

export default PECScanDataItemNewForm;